var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import * as React from 'react';
import { Fragment, useEffect } from 'react';
import { useAutoDisableValidate, useContextForm, useFormEffects, useFormState } from '../hooks';
import { internalLog, triggerModified } from '@ecom-zform/core';
import { useAutoDelete } from '../hooks/use-auto-delete';
import { isEqual, isFn, isStr, Keys, omit, pick } from '@ecom-zform/shared';
import { useChangeFormError } from '../hooks/use-change-form-error';
var defaultProps = {
    validateTrigger: ['onChange'],
};
var RenderComponent = function (originProps) {
    var props = __assign(__assign({}, originProps), { validateTrigger: originProps.validateTrigger || defaultProps.validateTrigger });
    var form = props.form;
    var state = useFormState(props.form, { sync: true });
    var value = state.value;
    // 当组件卸载后，不再对表单进行校验
    useAutoDisableValidate(form, props.autoDisableValidate === false);
    // 当 autoDelete 为 true 时，组件卸载时自动删除字段值
    useAutoDelete(form, !props.autoDelete);
    useFormEffects(form, props.effects || {});
    useEffect(function () {
        var list = [];
        if (props.onStateChange) {
            var stateTypes = Keys(props.onStateChange);
            stateTypes.forEach(function (type) {
                if (props.onStateChange) {
                    var callback = props.onStateChange[type];
                    if (type === 'state') {
                        // @ts-ignore
                        list.push(form.watchState(callback));
                    }
                    else {
                        // @ts-ignore
                        list.push(form.watchState(type, callback));
                    }
                }
            });
        }
        return function () { return list.forEach(function (f) { return f(); }); };
        /**
         * record:
         * 这里需要加入 deps，是因为当 onStateChange 中依赖了上下文中的 state，
         * 就需要在 onStateChange 渲染时，重新进行 watch
         * 如果不重新进行 watch，就会导致 state（指 react 上下文的 state）为旧的状态
         */
    }, [props.onStateChange]);
    if (!state.visible)
        return null;
    var renderDecorator = function (children) {
        if (!props.decorator) {
            return React.createElement(Fragment, null, children);
        }
        var required = state.required !== undefined
            ? state.required
            : Boolean(state.rules &&
                state.rules.some(function (rule) {
                    return !!(rule && typeof rule === 'object' && rule.required);
                }));
        var omitProps = omit(props, ['decorator', 'defaultState', 'form', 'autoDelete']);
        return React.createElement(props.decorator, __assign(__assign(__assign({ ref: function (ref) {
                if (!form.state.decorator) {
                    form.setState({
                        decorator: {
                            component: ref,
                            // 暂时先不存储状态，会出现性能问题
                            // props: omit(omitProps, ['children']),
                            props: {},
                        },
                    }, internalLog('set decorator', form));
                }
            } }, pick(state, [
            'tooltip',
            'label',
            'disabled',
            'description',
            'rules',
            'loading',
            'error',
            'validating',
        ])), { required: required }), omitProps), children);
    };
    if (!React.isValidElement(props.children) && !isFn(props.children)) {
        return renderDecorator(props.children);
    }
    var validateTrigger = Array.isArray(props.validateTrigger)
        ? props.validateTrigger
        : [props.validateTrigger];
    var validateTriggerEvents = {};
    var node = (isFn(props.children) ? props.children(state) : props.children);
    var old = props.mapValue ? props.mapValue(value) : value;
    // validateTrigger 逻辑
    validateTrigger.forEach(function (eventName) {
        validateTriggerEvents[eventName] = function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            if (node.props[eventName]) {
                (_a = node.props)[eventName].apply(_a, __spreadArray([], __read(args)));
            }
            form.validateWithAllParent(eventName);
        };
    });
    var stateProps = {};
    if (props.stateProps) {
        stateProps = pick(state, Keys(props.stateProps));
        Keys(props.stateProps).forEach(function (key) {
            if (props.stateProps && isStr(props.stateProps[key])) {
                var mapPropName = props.stateProps[key];
                stateProps[mapPropName] = stateProps[key];
                delete stateProps[key];
            }
        });
    }
    if (props.useInnerState) {
        props.useInnerState.forEach(function (key) {
            stateProps[key] = state[key];
        });
    }
    var renderComponent = function () {
        var _a;
        return React.cloneElement(node, __assign(__assign(__assign(__assign(__assign({}, stateProps), node.props), (_a = {}, _a[props.valuePropName || 'value'] = old, _a)), validateTriggerEvents), { onChange: function () {
                var args = [];
                for (var _i = 0; _i < arguments.length; _i++) {
                    args[_i] = arguments[_i];
                }
                var _a = __read(args, 1), e = _a[0];
                // TODO 判断优雅一点 有抽象的函数
                var newVal = props.getValueFromEvent
                    ? props.getValueFromEvent.apply(props, __spreadArray([], __read(args))) : typeof e === 'object' && e != null && 'preventDefault' in e
                    ? e.target.value
                    : e;
                // TODO onUpdateValue 是不是可以支持异步？
                var newV = props.onUpdateValue ? props.onUpdateValue(newVal, old) : newVal;
                form.setState({
                    value: newV,
                }, internalLog('fieldWrapper onChange', form));
                /**
                 * 表单组件（子节点的也一样）被 FormItem || FieldWrapper 包裹后，当用户对表单进行修改 modified 将变为 true
                 */
                if (!form.state.modified) {
                    triggerModified(form, 'fieldWrapper set self & allParent will set modified -> true');
                }
                if (validateTrigger.includes('onChange')) {
                    // 表单值发生变更时触发校验
                    form.validateWithAllParent('onChange');
                }
                var oldOnChange = node.props.onChange;
                if (oldOnChange) {
                    oldOnChange(newVal);
                }
            } }));
    };
    return renderDecorator(renderComponent());
};
/**
 * 将包含 onChange 和 value 属性的组件变为受控组件
 * 需要注意的是 FieldWrapper 里必须有且只有一个接受 value 和 onChange 属性的组件
 *
 * 详细使用文档：https://zform.bytedance.net/v2/docs/api/react/component/field-wrapper
 */
export var FieldWrapper = function (props) {
    var realForm = props.form || useContextForm();
    var initialized = useFormState(realForm, 'initialized');
    useChangeFormError(realForm, 'FieldWrapper');
    useEffect(function () {
        if (!initialized) {
            realForm.initialize(__assign({}, props.defaultState));
        }
    }, []);
    useEffect(function () {
        // 处理通过 props 传入 rules
        if (props.rules && !isEqual(realForm.state.rules, props.rules)) {
            realForm.setState({
                rules: props.rules,
            }, internalLog('fieldWrapper set rules', realForm));
        }
    }, [props.rules]);
    return (React.createElement(RenderComponent, __assign({}, props, { form: realForm }), props.children));
};
